/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ComponentType, FC, useState } from "react";
import Image, { StaticImageData } from "next/image";
import { motion } from "framer-motion";

import styles from "./styles.module.scss";
import { animationProps } from "./constants";

type Layer = {
  src?: StaticImageData;
  Component?: ComponentType<any>;
  width: number | string;
  height: number | string;
  motion?: boolean;
  componentProps?: { [key: string]: any };
};

type Props = {
  layers: Layer[];
  width: string | number;
  height: string | number;
  when?: "onHover" | "infinite";
  layout?: "fixed" | "responsive";
};

const AnimatedMandala: FC<Props> = ({
  layout = "fixed",
  layers,
  width,
  height,
  when = "onHover"
}) => {
  const [animationState, setAnimationState] = useState(false);

  const layerVariants = {
    forward: (index: number) => ({
      transition: {
        duration: 40 + (layers.length - index) * 40,
        repeat: Infinity,
        type: "linear",
        ease: "linear"
      },
      transform: "rotate(360deg)"
    }),
    backwards: (index: number) => ({
      transition: {
        duration: 40 + (layers.length - index) * 40,
        repeat: Infinity,
        type: "linear",
        ease: "linear"
      },
      transform: "rotate(-360deg)"
    }),
    stop: () => ({
      transition: {
        duration: 2
      }
    })
  };

  return (
    <motion.div
      {...animationProps(setAnimationState)(when)}
      className={styles.container}
      style={{ width, height }}
    >
      {layers.map(
        (
          {
            src,
            width: layerWidth,
            height: layerHeight,
            motion: layerMotion = true,
            Component,
            componentProps = {}
          },
          index
        ) => (
          <div
            key={src?.src || Component?.displayName}
            className={styles.layer}
          >
            <motion.div
              animate={
                animationState && layerMotion
                  ? index % 2
                    ? "forward"
                    : "backwards"
                  : "stop"
              }
              variants={layerVariants}
              custom={index}
              style={{ width: layerWidth, height: layerHeight }}
            >
              {src ? (
                <Image
                  loading="eager"
                  layout={layout}
                  src={src}
                  height={layerHeight}
                  width={layerWidth}
                />
              ) : Component ? (
                <Component
                  height={layerHeight}
                  width={layerWidth}
                  {...componentProps}
                />
              ) : null}
            </motion.div>
          </div>
        )
      )}
    </motion.div>
  );
};

AnimatedMandala.defaultProps = {
  when: "onHover",
  layout: "fixed"
};

export default AnimatedMandala;
